/**
 * Adapter store: helpers tipados de e-commerce.
 *
 * Cada helper llama a `getTelemetry()?.track(type, properties)` con el tipo de
 * dominio correcto y las propiedades enriquecidas. El enriquecimiento vive AQUÍ
 * (en el borde), no en el servicio central de telemetría.
 *
 * `balancePoints` (atPoints) NO se pasa en cada helper: el bridge lo setea en
 * `baseProperties` y el cliente lo mergea en `properties` de cada evento.
 */
import { getTelemetry } from '../client';
import { StoreEventTypes } from './events';

interface ProductRef {
  /** Id del premio/producto (award_id). */
  productId: string;
  /** Costo en puntos del premio (awardPoints). */
  productPoints?: number;
  categoryId?: string;
  subcategoryId?: string;
  /** Producto (product template) al que pertenece la variante, si tiene. */
  templateId?: string;
}

const track = (type: string, properties: Record<string, unknown>): void => {
  getTelemetry()?.track(type, properties);
};

// ─── Descubrimiento ──────────────────────────────────────────────────────────

export function trackProductView(p: ProductRef): void {
  track(StoreEventTypes.VIEW_PRODUCT, {
    productId: p.productId,
    productPoints: p.productPoints,
    templateId: p.templateId,
    categoryId: p.categoryId,
    subcategoryId: p.subcategoryId,
  });
}

export function trackQuickView(p: ProductRef): void {
  track(StoreEventTypes.QUICK_VIEW_PRODUCT, {
    productId: p.productId,
    productPoints: p.productPoints,
    templateId: p.templateId,
    categoryId: p.categoryId,
  });
}

export function trackCategory(categoryId: string): void {
  track(StoreEventTypes.VIEW_CATEGORY, { categoryId });
}

export function trackSubcategory(subcategoryId: string): void {
  track(StoreEventTypes.VIEW_SUBCATEGORY, { subcategoryId });
}

// Se emite cuando la búsqueda YA resolvió (con el resultado), no en el submit,
// para poder observar términos con 0 resultados, la distribución de conteos y el
// aporte de cada brazo (exacto/léxico/semántico) que llega en el searchMeta.
export function trackSearch(text: string, meta?: Record<string, unknown>): void {
  track(StoreEventTypes.SEARCH, { searchText: text, ...meta });
}

export function trackViewPromotion(metadata?: Record<string, unknown>): void {
  track(StoreEventTypes.VIEW_PROMOTION, { ...metadata });
}

// ─── Listados ────────────────────────────────────────────────────────────────

export function trackClickMoreFeatured(): void {
  track(StoreEventTypes.CLICK_MORE_FEATURED, {});
}

export function trackClickMoreMostRedeemed(): void {
  track(StoreEventTypes.CLICK_MORE_MOST_REDEEMED, {});
}

export function trackClickMoreReachable(): void {
  track(StoreEventTypes.CLICK_MORE_REACHABLE, {});
}

export function trackClickMoreFromCategory(categoryId: string): void {
  track(StoreEventTypes.CLICK_MORE_FROM_CATEGORY, { categoryId });
}

// ─── Wishlist ────────────────────────────────────────────────────────────────

export function trackAddToWishlist(p: ProductRef & { wishlistId?: string }): void {
  track(StoreEventTypes.ADD_TO_WISHLIST, {
    productId: p.productId,
    productPoints: p.productPoints,
    templateId: p.templateId,
    categoryId: p.categoryId,
    wishlistId: p.wishlistId,
  });
}

export function trackRemoveFromWishlist(p: ProductRef & { wishlistId?: string }): void {
  track(StoreEventTypes.REMOVE_FROM_WISHLIST, {
    productId: p.productId,
    productPoints: p.productPoints,
    templateId: p.templateId,
    categoryId: p.categoryId,
    wishlistId: p.wishlistId,
  });
}

// ─── Carrito ─────────────────────────────────────────────────────────────────

export function trackAddToCart(p: ProductRef & { quantity?: number }): void {
  track(StoreEventTypes.ADD_TO_CART, {
    productId: p.productId,
    productPoints: p.productPoints,
    templateId: p.templateId,
    categoryId: p.categoryId,
    quantity: p.quantity,
  });
}

export function trackRemoveFromCart(p: ProductRef): void {
  track(StoreEventTypes.REMOVE_FROM_CART, {
    productId: p.productId,
    productPoints: p.productPoints,
    templateId: p.templateId,
    categoryId: p.categoryId,
  });
}

export function trackUpdateCartQuantity(p: ProductRef & { quantity: number }): void {
  track(StoreEventTypes.UPDATE_CART_QUANTITY, {
    productId: p.productId,
    productPoints: p.productPoints,
    templateId: p.templateId,
    quantity: p.quantity,
  });
}

export function trackViewCart(metadata?: Record<string, unknown>): void {
  track(StoreEventTypes.VIEW_CART, { ...metadata });
}

// ─── Checkout / canje ────────────────────────────────────────────────────────

export function trackBeginCheckout(metadata?: Record<string, unknown>): void {
  track(StoreEventTypes.BEGIN_CHECKOUT, { ...metadata });
}

export function trackRedeem(params: {
  requestId?: string;
  points?: number;
  items?: Array<{ productId: string; quantity: number; productPoints?: number }>;
}): void {
  track(StoreEventTypes.REDEEM, {
    requestId: params.requestId,
    points: params.points,
    items: params.items,
  });
}

export type RedeemFailureReason =
  | 'store_read_only'
  | 'already_in_flight'
  | 'empty_cart'
  | 'no_participant'
  | 'invalid_item'
  | 'mixed_delivery_types'
  | 'invalid_address'
  | 'insufficient_points'
  | 'auth_expired'
  | 'network_error'
  | 'timeout'
  | 'backend_rejected'
  | 'server_error'
  | 'unknown';

export type RedeemResult = {
  status: 'success' | 'partial' | 'failed';
  itemsRedeemed: number;
  totalItems: number;
  failedAt?: number;
  failedItemName?: string;
  failedItemAwardId?: number;
  reason?: RedeemFailureReason;
  httpStatus?: number;
  httpMessage?: string;
  axiosCode?: string;
};

export function trackRedeemFailed(result?: Partial<RedeemResult> & { reason?: string }): void {
  track(StoreEventTypes.REDEEM_FAILED, {
    reason: result?.reason ?? 'unknown',
    itemsRedeemed: result?.itemsRedeemed,
    totalItems: result?.totalItems,
    failedAt: result?.failedAt,
    failedItemName: result?.failedItemName,
    failedItemAwardId: result?.failedItemAwardId,
    httpStatus: result?.httpStatus,
    httpMessage: result?.httpMessage,
    axiosCode: result?.axiosCode,
  });
}

export function trackPointsInsufficient(p: ProductRef): void {
  track(StoreEventTypes.POINTS_INSUFFICIENT, {
    productId: p.productId,
    productPoints: p.productPoints,
    templateId: p.templateId,
    categoryId: p.categoryId,
  });
}

// ─── Cuenta ──────────────────────────────────────────────────────────────────

export function trackViewMyRequests(): void {
  track(StoreEventTypes.VIEW_MY_REQUESTS, {});
}

export function trackViewAccountBalance(): void {
  track(StoreEventTypes.VIEW_ACCOUNT_BALANCE, {});
}
